import React, { useState, useEffect } from 'react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { TOPIC_CATEGORIES_ENDPOINT } from '../config/api';
import { SHARED_CHART_COLORS } from '../constants/chartColors';
import { getChartCache, setChartCache, CHART_KEYS } from '../utils/cache';
import './TopicCategoriesChart.css';

function toProperCase(str) {
  return (str || '').trim().replace(/\b\w/g, (c) => c.toUpperCase());
}

function buildChartData(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const filtered = rows.filter((d) => {
    const cat = (d.category || '').trim().toLowerCase();
    return cat && cat !== 'other';
  });
  return filtered
    .map((d) => ({
      name: (d.category || '').trim() || 'other',
      sessions: Number(d.session_id_count) || 0,
    }))
    .sort((a, b) => b.sessions - a.sessions);
}

/** Sample bar data for new users: same shape as chartData ({ name, sessions }). */
function getSampleTopicChartData() {
  return [
    { name: 'work', sessions: 5 },
    { name: 'relationships', sessions: 4 },
    { name: 'self-care', sessions: 3 },
    { name: 'health', sessions: 2 },
    { name: 'growth', sessions: 2 },
  ];
}

const MOBILE_BREAKPOINT = 768;

function TopicCategoriesChart() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNarrowViewport, setIsNarrowViewport] = useState(() => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handler = () => setIsNarrowViewport(mq.matches);
    handler();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const user = await getCurrentUser();
        const uid = user?.userId;
        const cached = uid ? getChartCache(CHART_KEYS.topics, uid) : null;
        if (!cancelled && cached != null && Array.isArray(cached) && cached.length > 0) {
          setData(cached);
          setLoading(false);
        }
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        const res = await fetch(TOPIC_CATEGORIES_ENDPOINT, {
          headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error('Failed to load topic categories');
        const json = await res.json();
        const payload = json?.statusCode != null ? JSON.parse(json.body || '[]') : json;
        const list = Array.isArray(payload) ? payload : [];
        if (!cancelled) {
          setData(list);
          setError(null);
          if (uid) setChartCache(CHART_KEYS.topics, uid, list);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading topic categories:', err);
          setError(err.message);
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="topic-categories-chart-container">
        <div className="topic-categories-chart-loading">Loading topics...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="topic-categories-chart-container">
        <div className="topic-categories-chart-error">Unable to load topics</div>
      </div>
    );
  }

  const chartDataFromApi = buildChartData(data || []);
  const hasRealData = chartDataFromApi.length > 0;
  const chartData = hasRealData ? chartDataFromApi : getSampleTopicChartData();

  const chartMargin = isNarrowViewport ? { top: 16, right: 16, bottom: 24, left: 28 } : { top: 16, right: 24, bottom: 16, left: 20 };
  const xAxisTickStyle = isNarrowViewport ? { fontSize: 9, fontWeight: 600 } : { fontSize: 11, fontWeight: 600 };
  const yAxisLabelStyle = isNarrowViewport ? { fontSize: 11, fill: '#111827', fontWeight: 600 } : { fontSize: 14, fill: '#111827', fontWeight: 600 };

  return (
    <div className="topic-categories-chart-container">
      {!hasRealData && (
        <div className="chart-cta-new-user" role="status">
          <p className="chart-cta-new-user-headline">Start journalling to see your data.</p>
          <p className="chart-cta-new-user-note">This is a sample view.</p>
        </div>
      )}
      <div className="topic-categories-chart-body">
        <ResponsiveContainer width="100%" height={320} minWidth={320}>
          <BarChart
            data={chartData}
            margin={chartMargin}
          >
            <XAxis
              type="category"
              dataKey="name"
              tick={xAxisTickStyle}
              tickLine={false}
              axisLine={{ stroke: '#9ca3af', strokeWidth: 1.5 }}
              tickFormatter={(v) => toProperCase(v)}
              interval={isNarrowViewport ? Math.ceil((chartData.length - 1) / 4) : 0}
            />
            <YAxis
              type="number"
              allowDecimals={false}
              tick={false}
              domain={[0, (dataMax) => Math.ceil((dataMax || 1) * 1.15)]}
              axisLine={{ stroke: '#9ca3af', strokeWidth: 1.5 }}
              tickLine={{ stroke: '#9ca3af', strokeWidth: 1.5 }}
              label={{
                value: 'Number of sessions',
                angle: -90,
                position: 'center',
                offset: isNarrowViewport ? -6 : -8,
                style: yAxisLabelStyle,
              }}
            />
            <Tooltip
              cursor={{ fill: 'rgba(0,0,0,0.04)' }}
              content={({ active, payload }) => {
                if (!active || !payload || !payload.length) return null;
                const d = payload[0].payload;
                return (
                  <div className="topic-categories-chart-tooltip">
                    <div className="topic-categories-chart-tooltip-label">{toProperCase(d.name)}</div>
                    <div className="topic-categories-chart-tooltip-value">
                      {d.sessions} session{d.sessions === 1 ? '' : 's'}
                    </div>
                  </div>
                );
              }}
            />
            <Bar dataKey="sessions" radius={[4, 4, 0, 0]} maxBarSize={48}>
              <LabelList
                dataKey="sessions"
                position="top"
                offset={6}
                style={{ fontSize: 11, fill: '#6b7280' }}
              />
              {chartData.map((d, i) => (
                <Cell key={i} fill={SHARED_CHART_COLORS[i % SHARED_CHART_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default TopicCategoriesChart;
